import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authApi } from '../services/api';
import { checkAuthenticationWithRetry, buildLoginRedirectUrl } from '../utils/authHelper';

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // Check if user has token and is logged in
        const loggedIn = authApi.isLoggedIn();

        if (loggedIn) {
          // Verify token with timeout and retry logic
          const result = await checkAuthenticationWithRetry();

          if (result.success) {
            setIsAuthenticated(true);
          } else {
            // Authentication failed after retries - logout and redirect
            authApi.logout();
            setIsAuthenticated(false);
          }
        } else {
          setIsAuthenticated(false);
        }
      } catch (err: unknown) {
        console.error('Authentication check failed:', err);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthentication();
  }, []);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Preserve the attempted URL to redirect back after login
    const redirectPath = location.pathname + location.search;
    const loginPath = redirectPath === '/login'
      ? '/login'
      : buildLoginRedirectUrl(redirectPath);

    return <Navigate to={loginPath} replace />;
  }

  // User is authenticated, render children
  return <>{children}</>;
};

export default AuthGuard;
